"use client";

import { Loader2, Workflow } from "lucide-react";
import Image from "next/image";
import type { RefObject } from "react";

import { displayChatTitle, type ChatHistoryItem } from "@/features/chat/multi/history/chat-history";
import { cn } from "@/shared/lib/utils/cn";
import { SkeletonChatRows } from "@/shared/ui/loading";
import { SimpleTooltip } from "@/shared/ui/simple-tooltip";
import { SidebarChatRow } from "./SidebarChatRow";
import { ChatRowStatusDot } from "./ChatRowStatusDot";
import { useChatRowFlags } from "./useChatRowFlags";

interface SidebarChatListProps {
  collapsed: boolean;
  pathname: string;
  chatsReady: boolean;
  query: string;
  onQueryChange: (value: string) => void;
  filteredCount: number;
  visibleChats: ChatHistoryItem[];
  hasMoreChats: boolean;
  loadingMore: boolean;
  activeSessionId: string | null;
  loadMoreRef: RefObject<HTMLDivElement | null>;
  chatScrollAreaRef: RefObject<HTMLDivElement | null>;
  activeChatRef: RefObject<HTMLButtonElement | null>;
  onOpenChat: (id: string) => void;
  onOpenWorkflowCanvas?: (id: string) => void;
  onPinChat: (chat: ChatHistoryItem) => void;
  onRenameChat: (chat: ChatHistoryItem) => void;
  onShareChat: (chat: ChatHistoryItem) => void;
  onDeleteChat: (chat: ChatHistoryItem) => void;
}

function CollapsedChatButton({
  chat,
  isActive,
  onOpenChat,
  onOpenWorkflowCanvas,
}: {
  chat: ChatHistoryItem;
  isActive: boolean;
  onOpenChat: (id: string) => void;
  onOpenWorkflowCanvas?: (id: string) => void;
}) {
  const title = displayChatTitle(chat);
  const { status } = useChatRowFlags(chat.id);

  return (
    <SimpleTooltip
      content={
        status === "building"
          ? `${title} — building`
          : status === "needs_input"
            ? `${title} — needs your input`
            : title
      }
      side="right"
    >
      <button
        type="button"
        aria-current={isActive ? "page" : undefined}
        onClick={() =>
          status === "building" && onOpenWorkflowCanvas
            ? onOpenWorkflowCanvas(chat.id)
            : onOpenChat(chat.id)
        }
        className={cn(
          "relative flex h-[39px] w-full items-center justify-center rounded-xl transition-colors",
          isActive
            ? "bg-card text-primary"
            : "text-muted-foreground hover:bg-muted/50 hover:text-primary",
        )}
      >
        <span className="flex size-[26px] items-center justify-center rounded-lg bg-[rgba(245,245,246,0.6)]">
          {status === "building" ? (
            <Workflow className="size-3.5 animate-pulse text-primary" />
          ) : (
            <Image src="/figma-assets/bot.svg" alt="" width={14} height={14} />
          )}
        </span>
        <ChatRowStatusDot status={status} className="absolute right-0.5 bottom-0.5" />
      </button>
    </SimpleTooltip>
  );
}

export function SidebarChatList({
  collapsed,
  pathname,
  chatsReady,
  query,
  onQueryChange,
  filteredCount,
  visibleChats,
  hasMoreChats,
  loadingMore,
  activeSessionId,
  loadMoreRef,
  chatScrollAreaRef,
  activeChatRef,
  onOpenChat,
  onOpenWorkflowCanvas,
  onPinChat,
  onRenameChat,
  onShareChat,
  onDeleteChat,
}: SidebarChatListProps) {
  if (collapsed) {
    return (
      <div className="min-h-0 flex-1 border-t border-sidebar-border px-2 py-2">
        <div className="space-y-0.5">
          {!chatsReady
            ? Array.from({ length: 5 }, (_, index) => (
                <div key={index} className="flex h-[39px] items-center justify-center">
                  <div className="size-[26px] animate-pulse rounded-lg bg-muted" />
                </div>
              ))
            : visibleChats
                .slice(0, 8)
                .map((chat) => (
                  <CollapsedChatButton
                    key={chat.id}
                    chat={chat}
                    isActive={pathname.startsWith("/flow-builder") && activeSessionId === chat.id}
                    onOpenChat={onOpenChat}
                    onOpenWorkflowCanvas={onOpenWorkflowCanvas}
                  />
                ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="shrink-0 border-t border-sidebar-border px-3 pt-1.5 pb-1">
        <div className="relative h-7 w-full">
          <span className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 overflow-hidden">
            <Image
              src="/figma-assets/search-chats.svg"
              alt=""
              width={14}
              height={14}
              className="size-3.5"
            />
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search chats..."
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            className="h-7 w-full rounded-lg border border-sidebar-border bg-background/60 pr-3 pl-[33px] text-[12px] transition-colors placeholder:text-muted-foreground/40 focus:border-primary/30 focus:bg-card focus:ring-1 focus:ring-primary/10 focus:outline-none"
          />
        </div>
      </div>

      <div
        ref={chatScrollAreaRef}
        data-trace="chats-scroll"
        className="min-h-0 w-full min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain"
      >
        <div data-trace="chats" className="w-full min-w-0 space-y-0.5 px-3 pt-1 pb-2">
          {!chatsReady ? <SkeletonChatRows count={6} /> : null}

          {chatsReady && filteredCount === 0 ? (
            <p className="px-3 py-4 text-center text-[11px] text-muted-foreground/70">
              {query.trim() ? "No chats match your search" : "No chats yet"}
            </p>
          ) : null}

          {chatsReady
            ? visibleChats.map((chat) => {
                const isActive =
                  pathname.startsWith("/flow-builder") && activeSessionId === chat.id;
                return (
                  <SidebarChatRow
                    key={chat.id}
                    chat={chat}
                    isActive={isActive}
                    activeChatRef={isActive ? activeChatRef : undefined}
                    onOpenChat={onOpenChat}
                    onOpenWorkflowCanvas={onOpenWorkflowCanvas}
                    onPinChat={onPinChat}
                    onRenameChat={onRenameChat}
                    onShareChat={onShareChat}
                    onDeleteChat={onDeleteChat}
                  />
                );
              })
            : null}

          {chatsReady && hasMoreChats ? (
            <div
              ref={loadMoreRef}
              className="flex items-center justify-center gap-1.5 py-3"
              aria-hidden={!loadingMore}
            >
              {loadingMore ? (
                <>
                  <Loader2 className="size-3.5 animate-spin text-muted-foreground/60" />
                  <span className="text-[10px] text-muted-foreground/60">Loading…</span>
                </>
              ) : (
                <span className="text-[10px] text-muted-foreground/40">Scroll for more</span>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
